import { Project, response } from "../models";
import { ApiClient } from "../network/ApiClient";
import { ApiEndpoints } from "../network/ApiEndpoints";

class ProjectService {
  public async get(id: number): Promise<Project> {
    return Promise.resolve({ id: id } as Project);
  }

  public async create(payload: Project | any): Promise<response> {
    const response: response = await ApiClient.post(
      ApiEndpoints.createProject(),
      {
        projectName: payload.projectName,
        estimatedCompletionDate: payload.estimatedCompletionDate || null,
        counterParties: payload.counterParties,
        projectOwner: payload.projectOwner,
        description: payload.description || null,
        projectValue: payload.projectValue ? parseFloat(String(payload.projectValue)) : 0,
        projectStatus: payload.projectStatus || "Active",
        clientNumber: payload.clientNumber,
        clientMatter: payload.clientMatter,
      },
      {},
      {}
    );
    return Promise.resolve(response);
  }

  public async update(id: number, payload: Project): Promise<Project> {
    return Promise.resolve({ ...payload, id: id } as Project);
  }

  public async updateDetails(payload: any): Promise<response> {
    // Ensure Status is provided - required non-nullable field
    if (!payload.projectStatus && !payload.status) {
      throw new Error("Project status is required");
    }
    
    const response: response = await ApiClient.put(
      ApiEndpoints.updateProjectDetails(),
      {
        projectId: payload.projectId,
        projectName: payload.projectName,
        projectValue: payload.projectValue,
        projectOwner: payload.projectOwner,
        projectStatus: payload.projectStatus || payload.status,
        counterParties: payload.counterParties,
        estimatedCompletionDate: payload.estimatedCompletionDate 
          ? new Date(payload.estimatedCompletionDate).toISOString()
          : null,
        description: payload.description,
        clientNumber: payload.clientNumber,
        clientMatter: payload.clientMatter,
      },
    );
    return Promise.resolve(response);
  }
  
  public async delete(id: string): Promise<void> {
    return ApiClient.delete("api-endpoint", { id: id });
  }

  public async changeStatusOfProject(payload: any): Promise<any> {
    const requestBody = {
      status: payload.status || null,
      value: payload.value || null,
    };
    const response = await ApiClient.patch(
      ApiEndpoints.changeStatusOfProject(payload.id),
      requestBody,
      {}
    );
    return Promise.resolve(response);
  }

  // DEX-573: GET /projects/statuses is fetched independently by both
  // ProjectDataButtonStack and FunctionalityButtonStack, which mount together on the
  // dashboard — firing two identical requests. Share one in-flight promise so concurrent
  // callers issue a single request; cleared on settle, so a later call refetches fresh
  // (no stale counts after a project is created/closed).
  private statusesInFlight: Promise<any> | null = null;

  public async getStatuses() {
    if (this.statusesInFlight) return this.statusesInFlight;
    this.statusesInFlight = ApiClient.get(ApiEndpoints.getStatusesOfProject()).finally(
      () => {
        this.statusesInFlight = null;
      }
    );
    return this.statusesInFlight;
  }

  public async projectInfo(id: number) {
    const response = await ApiClient.get(ApiEndpoints.projectInfo(id));
    return Promise.resolve(response);
  }
  // DEX-573: GET /permissions (the project-limit config) is fetched by Homepage,
  // AllProjectData, and CreateDocumentModal — often more than once per view. The limit is
  // static per session (it changes with the subscription tier, and login forces a reload),
  // so cache the promise for the session: concurrent and sequential callers all share one
  // request. Cleared on failure so a retry can refetch.
  private projectLimitPromise: Promise<response> | null = null;

  public async getProjectLimit(): Promise<response> {
    if (this.projectLimitPromise) return this.projectLimitPromise;
    this.projectLimitPromise = ApiClient.get<response>(ApiEndpoints.getProjectLimit());
    this.projectLimitPromise.catch(() => {
      this.projectLimitPromise = null;
    });
    return this.projectLimitPromise;
  }
  public async getProjectDocuments(id: number) {
    const response = await ApiClient.get(ApiEndpoints.getProjectDocuments(id));
    return Promise.resolve(response);
  }
}

const projectService = new ProjectService();
export { projectService as ProjectService };
